import { IBill } from "@/models/bill/bill.types"
import { BillParser } from "@/models/bill/bill.parser"
import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"
import { mapQueryResult } from "@/api/utils/mapQuery"
import { AggregatedQueryProps } from "@/api/utils/QueryAggregator"
import { BillBreakdownLineParser } from "@/models/billBreakdown/billBreakdown.parser"
import { IBillBreakdownLine } from "@/models/billBreakdown/billBreakdown.types"
import { BillEmailParser } from "@/models/billEmail/billEmail.parser"
import { IBillEmail } from "@/models/billEmail/billEmail.types"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import {
  AccountBill,
  BillBreakdownLine,
  BillEmail,
  BillsZipLinkResponse,
  FileContainer,
} from "@/api/backofficeRest/generated/model"
import { base64ToBlob } from "@/helpers/downloadFile"
import { errorReporter } from "@/services/ErrorService"

export interface IBillService {
  getForAccount(billingAccountId: number | null): QueryResult<IBill[]>
  getLatestBill(
    billingAccountId: number | null,
  ): AggregatedQueryProps<IBill | undefined>
  getBill(billId: number, billingAccountId: number | null): QueryResult<IBill>
  getBillBreakdown(billId: number): QueryResult<IBillBreakdownLine[]>
  getBillEmail(billId: number): QueryResult<IBillEmail>
}

class BillServiceClass implements IBillService {
  getForAccount(billingAccountId: number | null): QueryResult<IBill[]> {
    return useQuery({
      ...BackofficeRestQueries.billing.bills(billingAccountId ?? 0),
      placeholderData: [],
      useRawResponse: true,
      enabled: !!billingAccountId,
      // Soft-deleted bills stay in the list: Junifer still reports their
      // transactions, which join against this list to render bill details.
      parserFn: (rawData: AccountBill[]) =>
        (rawData ?? []).flatMap((rawBill) => {
          // One malformed bill must not blank the account's whole list.
          try {
            return [BillParser.toDomain(rawBill)]
          } catch (error) {
            errorReporter.report(error)
            return []
          }
        }),
    })
  }
  getLatestBill(billingAccountId: number | null) {
    return mapQueryResult(
      this.getForAccount(billingAccountId),
      (query) =>
        query.data
          .filter((bill) => !bill.isDeleted)
          .sort((a, b) => (a.periodTo?.isBefore(b.periodTo) ? 1 : -1))[0],
    )
  }
  getBill(billId: number, billingAccountId: number | null): QueryResult<IBill> {
    return useQuery({
      ...BackofficeRestQueries.billing.bill(billId, billingAccountId ?? 0),
      useRawResponse: true,
      enabled: !!billId && !!billingAccountId,
      parserFn: (rawData: AccountBill) => {
        if (rawData && !rawData.deleted) {
          return BillParser.toDomain(rawData)
        }
        throw new Error(`Bill not found: ${billId}`)
      },
    })
  }
  getBillBreakdown(billId: number): QueryResult<IBillBreakdownLine[]> {
    return useQuery({
      ...BackofficeRestQueries.billing.billBreakdown(billId),
      placeholderData: [],
      useRawResponse: true,
      enabled: !!billId,
      parserFn: (rawData: BillBreakdownLine[]) =>
        (rawData ?? []).map((rawBillBreakdownLine) =>
          BillBreakdownLineParser.toDomain(rawBillBreakdownLine),
        ),
    })
  }
  getBillEmail(billId: number): QueryResult<IBillEmail> {
    return useQuery({
      ...BackofficeRestQueries.billing.billEmail(billId),
      useRawResponse: true,
      enabled: !!billId,
      parserFn: (rawData: BillEmail) => {
        if (rawData) {
          return BillEmailParser.toDomain(rawData)
        }
        throw new Error(`Bill email not found: ${billId}`)
      },
    })
  }
  getBillImage(billId: number): QueryResult<Blob> {
    return useQuery({
      ...BackofficeRestQueries.billing.billImage(billId),
      useRawResponse: true,
      // Lazy: the PDF is only fetched via refetch() when a bill is downloaded.
      enabled: false,
      parserFn: (rawData: FileContainer) => {
        if (rawData) {
          return base64ToBlob(rawData.file, "application/pdf")
        }
        throw new Error(`Bill image not found: ${billId}`)
      },
    })
  }
  getBillsZipLinkByBillingAccountId(
    billingAccountId: number,
  ): QueryResult<string> {
    return useQuery({
      ...BackofficeRestQueries.billing.billsZipLink(billingAccountId),
      useRawResponse: true,
      enabled: true,
      parserFn: (rawData: BillsZipLinkResponse) => {
        const link = rawData?.link
        if (link) {
          return link
        }
        throw new Error("Link is not set in bills zip link response")
      },
    })
  }
}

export const BillService = new BillServiceClass()
